import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AbstractContentComponent } from '../../../../abstract-content.component';
import { Context } from '@service/context.service';
import { Column } from '@shared/table/column';
import { ChopProfil } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { ChopProfilDialogComponent } from './chop-profil-dialog/chop-profil-dialog.component';
import { ActionButtonDirective } from '@shared/table/action-button.directive';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { TableComponent } from '@shared/table/table.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChopLeistungsgruppeDetailData } from './chop-leistungsgruppe-detail.resolve';

@Component({
  selector: 'app-chop-leistungsgruppe-detail',
  templateUrl: './chop-leistungsgruppe-detail.component.html',
  styleUrls: ['./chop-leistungsgruppe-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableComponent, ToolbarButtonDirective, ActionButtonDirective],
})
export class ChopLeistungsgruppeDetailComponent extends AbstractContentComponent implements OnInit {
  private chopService = inject(ChopService);

  private data = this.activatedRoute.snapshot.data.detail as ChopLeistungsgruppeDetailData;
  private leistungsgruppe = this.data.leistungsgruppe;
  private alterskategorie = this.data.alterskategorie;
  private katalog = this.data.katalog;
  profile: ChopProfil[] = this.data.profile;
  columns: Column[] = [];
  private leistungsgruppeId = this.leistungsgruppe.id;

  constructor() {
    super(Context.Global);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.refreshBreadcrumbs();
  }

  protected onLanguageChange(): void {
    this.setColumns();
    this.refreshBreadcrumbs();
    this.markForCheck();
  }

  reload(): void {
    this.chopService.getProfile(this.leistungsgruppeId).subscribe((profile) => {
      this.profile = profile;
      this.markForCheck();
    });
  }

  showCreateDialog(): void {
    this.openDialog()?.onClose.subscribe((result) => {
      if (result) {
        this.reload();
      }
    });
  }

  showEditDialog(profil: ChopProfil): void {
    this.openDialog(profil)?.onClose.subscribe((result) => {
      if (result) {
        this.reload();
      }
    });
  }

  openDetail(profil: ChopProfil): void {
    this.navigate(['/admin-chop/profile', profil.id]);
  }

  delete(profil: ChopProfil): void {
    this.confirm({
      header: this.translate('CHOP.DELETE_PROFIL_HEADER'),
      message: this.translate('CHOP.DELETE_PROFIL_MESSAGE', {
        nummer: profil.nummer,
        bezeichnung: this.getLocalizedText(profil.bezeichnung),
      }),
      accept: () => {
        this.chopService.deleteProfil(profil.id).subscribe({
          next: () => {
            this.showSuccessMessage();
            this.reload();
          },
          error: (err) => this.showErrorMessage(undefined, err?.error?.message),
        });
      },
    });
  }

  private openDialog(profil?: ChopProfil): DynamicDialogRef | null {
    return this.dialogService.open(ChopProfilDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      width: '500px',
      header: this.translate(profil ? 'CHOP.EDIT_PROFIL' : 'CHOP.CREATE_PROFIL'),
      data: { leistungsgruppeId: this.leistungsgruppeId, profil },
    });
  }

  private refreshBreadcrumbs(): void {
    this.setBreadcrumbs([
      { label: this.translate('MENU.CHOP'), routerLink: ['/admin-chop'] },
      {
        label: `${this.katalog.jahr} ${this.getLocalizedText(this.katalog.bezeichnung)}`,
        routerLink: ['/admin-chop/kataloge', this.katalog.id],
      },
      {
        label: `${this.alterskategorie.code} ${this.getLocalizedText(this.alterskategorie.bezeichnung)}`,
        routerLink: ['/admin-chop/alterskategorien', this.alterskategorie.id],
      },
      { label: `${this.leistungsgruppe.nummer} ${this.getLocalizedText(this.leistungsgruppe.bezeichnung)}` },
    ]);
  }

  private setColumns(): void {
    this.columns = [
      { field: 'nummer', headerKey: 'CHOP.NUMMER', width: '120px' },
      { field: 'bezeichnung.De', headerKey: 'GLOBAL.NAME_De', minWidth: '200px' },
      { field: 'bezeichnung.Fr', headerKey: 'GLOBAL.NAME_Fr', minWidth: '200px' },
      { field: 'bezeichnung.It', headerKey: 'GLOBAL.NAME_It', minWidth: '200px' },
    ];
  }
}
